import { useRef, useState } from 'react';
import type { LodgingSegment } from '../../types';
import { fromMinor, money, toMinor, wholeStayNights } from '../../core';
import { newId, useActions, useStore } from '../../store';
import { TextField, MoneyField, Checkbox, Stepper, NoteField } from './fields';
import formShell from './formShell.module.css';
import { useT } from '../../i18n';

export interface LodgingFormProps {
  mode: 'create' | 'edit';
  segment?: LodgingSegment;
  onClose(): void;
}

export function LodgingForm({ mode, segment, onClose }: LodgingFormProps) {
  const t = useT();
  const actions = useActions();
  const state = useStore();

  const [city, setCity] = useState(segment?.city ?? '');
  const [nights, setNights] = useState(segment?.nights ?? 1);
  const [wholeStay, setWholeStay] = useState(segment?.wholeStay ?? false);

  // «На весь промежуток» разрешён, только когда за блоком в каком-нибудь
  // варианте стоит следующий блок — иначе считать ночи не до чего.
  const hasNext =
    segment !== undefined &&
    state.variants.some((v) => {
      const i = v.segmentIds.indexOf(segment.id);
      return i !== -1 && i < v.segmentIds.length - 1;
    });
  // Предпросчёт ночей — по первому варианту, где нашлись оба якоря-рейса.
  const preview =
    segment !== undefined
      ? state.variants
          .map((v) => wholeStayNights(segment.id, v.segmentIds, state))
          .find((n) => n !== null) ?? null
      : null;

  /** Включение опции сразу подставляет посчитанные ночи — их видно до сохранения. */
  function handleWholeStay(next: boolean) {
    setWholeStay(next);
    if (next && preview !== null) setNights(preview);
  }
  const [amount, setAmount] = useState(
    segment ? String(fromMinor(segment.pricePerNight.amount)) : '',
  );
  const [currency, setCurrency] = useState(segment?.pricePerNight.currency ?? 'USD');
  const [perPerson, setPerPerson] = useState(segment?.perPerson ?? false);
  const [note, setNote] = useState(segment?.note ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const refs = useRef<Record<string, HTMLElement | null>>({});

  function validate(): Record<string, string> {
    const errs: Record<string, string> = {};
    if (!city.trim()) errs.city = t('error.cityRequired');
    if (nights < 1) errs.nights = t('error.nightsMin');
    const amountNum = parseFloat(amount.replace(',', '.'));
    if (amount.trim() === '' || Number.isNaN(amountNum) || amountNum < 0) {
      errs.amount = t('error.priceNegative');
    }
    return errs;
  }

  function focusFirstError(errs: Record<string, string>) {
    for (const key of ['city', 'amount']) {
      if (errs[key]) {
        refs.current[key]?.focus();
        return;
      }
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstError(errs);
      return;
    }
    const built: LodgingSegment = {
      kind: 'lodging',
      id: segment?.id ?? newId('l'),
      city: city.trim(),
      // При «на весь промежуток» сохранённые ночи — запасное значение: расчёт
      // на доске идёт от соседних рейсов варианта (calc: wholeStayNights).
      nights: wholeStay && preview !== null ? preview : nights,
      pricePerNight: money(toMinor(amount), currency),
      perPerson,
      wholeStay: wholeStay || undefined,
      note: note.trim() || undefined,
    };
    if (mode === 'edit' && segment) {
      actions.updateSegment(built.id, built);
    } else {
      actions.addSegment(built);
    }
    onClose();
  }

  return (
    <form onSubmit={handleSubmit}>
      <TextField
        id="lodging-city"
        label={t('field.city')}
        value={city}
        onChange={setCity}
        required
        autoFocus
        error={errors.city}
        inputRef={(el) => {
          refs.current.city = el;
        }}
      />
      <Stepper
        id="lodging-nights"
        label={t('field.nights')}
        value={nights}
        onChange={setNights}
        min={1}
        disabled={wholeStay}
      />
      <Checkbox
        id="lodging-whole-stay"
        label={t('field.wholeStay')}
        checked={wholeStay}
        onChange={handleWholeStay}
        disabled={!hasNext}
        hint={t(hasNext ? 'field.wholeStay.hint' : 'field.wholeStay.need')}
      />
      <MoneyField
        idPrefix="lodging-price"
        label={t('field.pricePerNight')}
        amount={amount}
        onAmountChange={setAmount}
        currency={currency}
        onCurrencyChange={setCurrency}
        required
        error={errors.amount}
        inputRef={(el) => {
          refs.current.amount = el;
        }}
      />
      <Checkbox
        id="lodging-per-person"
        label={t('field.perPerson')}
        checked={perPerson}
        onChange={setPerPerson}
      />
      <NoteField id="lodging-note" value={note} onChange={setNote} />
      <div className={formShell.footer}>
        <button type="button" className="btn" onClick={onClose}>
          {t('form.cancel')}
        </button>
        <button type="submit" className="btn btn-primary">
          {t('form.save')}
        </button>
      </div>
    </form>
  );
}
